import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "wfcGateToken"; // keep key for back-compat; now holds a user JWT

const AuthContext = createContext({
  user: null,
  token: null,
  status: "loading", // loading | authed | unauthed
  login: async () => ({ ok: false }),
  logout: () => {},
  setUser: () => {},
});

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(token ? "loading" : "unauthed");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setStatus("unauthed");
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setUser(data);
          setStatus("authed");
        }
      } catch {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setUser(null);
          setStatus("unauthed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
      });
      localStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (e) {
      const detail = e?.response?.data?.detail || "Login failed";
      return { ok: false, error: typeof detail === "string" ? detail : "Login failed" };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
    setStatus("unauthed");
  }, []);

  // After accept-invite or password-reset, set the new token directly
  const setAuth = useCallback((newToken, newUser) => {
    if (newToken) {
      localStorage.setItem(STORAGE_KEY, newToken);
      setToken(newToken);
    }
    if (newUser) {
      setUser(newUser);
      setStatus("authed");
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, status, login, logout, setAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function getStoredToken() {
  return localStorage.getItem(STORAGE_KEY);
}
